import { useRosterProfile } from '@/hooks/equip/useRosterProfile'
import { useEffectiveEmploymentStatus } from '@/hooks/equip/useEffectiveEmploymentStatus'
import { useImpersonation } from '@/context/ImpersonationContext'
import { PhysicianLeaderGuide } from '@/pages/physician/PhysicianLeaderGuide'
import { NonPhysicianLeaderGuide } from '@/pages/non-physician/NonPhysicianLeaderGuide'

/**
 * UnifiedLeaderGuideGateway
 *
 * Routes to the correct leader guide based on the user's effective roster profile.
 * Effective profile = selectedPersona (if set by admin) ?? real API result.
 *
 * Physician track  → PhysicianLeaderGuide
 * APC track        → NonPhysicianLeaderGuide
 * No profile match → Fallback message
 *
 * Accessible to all EQUIP_PERFORMANCE_ROLES (including equip.read).
 * equip.read users see the leader guide for their cohort in read-only context.
 */
export function UnifiedLeaderGuideGateway() {
  const { data: profile, isLoading, error } = useRosterProfile()
  const { selectedPersona } = useImpersonation()
  const { effectiveEmploymentStatus } = useEffectiveEmploymentStatus()

  // Show spinner only when loading real profile AND no persona override
  if (isLoading && !selectedPersona) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-eventus-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Loading your leader guide...</p>
        </div>
      </div>
    )
  }

  const effectiveProfile = selectedPersona ?? profile

  if (error && !selectedPersona) {
    return <LeaderGuideFallback reason="error" />
  }

  if (!effectiveProfile || effectiveProfile.roleId === null) {
    return <LeaderGuideFallback reason="no-profile" />
  }

  if (effectiveProfile.isPhysician === true) {
    return <PhysicianLeaderGuide employmentStatus={effectiveEmploymentStatus} />
  }

  return <NonPhysicianLeaderGuide employmentStatus={effectiveEmploymentStatus} />
}

function LeaderGuideFallback({ reason }: { reason: 'no-profile' | 'error' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Leader Guide Not Available</h2>
        <p className="text-gray-600 text-sm leading-relaxed">
          {reason === 'error'
            ? 'We were unable to load your leader guide. Please try refreshing the page or contact support.'
            : 'Leader guide content is not currently available for your role. Contact your administrator if you believe this is an error.'}
        </p>
      </div>
    </div>
  )
}
